/**
 * Shared Nango "connect an integration" OAuth flow — this flow (POST
 * /integrations/nango/createConnectSession -> open Nango SDK popup -> SDK's
 * onEvent callback -> POST /integrations/nango/storeSession -> confirm via
 * GET /integrations/available and/or GET /accounts/location) was
 * independently reimplemented across:
 *   - pages/Settings/components/integration/Nango.js (Settings "Your Tools")
 *   - pages/IntegrationApp/Integration/Components/IntegrationCard.jsx
 *   - pages/Tasks/components/PromptModal/IntegrationModal/index.jsx
 *   - pages/YourAIAgents/components/IntegrationModal/index.jsx
 *
 * The first two of those tracked their post-connect confirmation poll in a
 * plain local `let` interval variable — never cleared on unmount, no
 * max-attempts guard, a real leak. useIntegrationConnectionPoll replaces
 * both with refetchInterval, which React Query cancels automatically once
 * the query is disabled (pass `enabled` only while a connect is in flight)
 * or the component unmounts — same pattern as scraping.js's
 * useScrapeJobStatus.
 *
 * createConnectSession/storeSession are intentionally thin passthroughs
 * (just the POST, no success-check baked in): the three call sites branch on
 * `{success, data, message}` slightly differently (different fallback error
 * text, one site toasts before throwing, another swallows errors silently),
 * so that branching stays in each component, same as before, driven off
 * these mutations' resolved/rejected value.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT } = ReqMethods;

const POLL_INTERVAL_MS = 4000;

export const integrationKeys = {
  all: ["integrations"],
  available: (locationId, userId, agencyId) => [
    ...integrationKeys.all,
    "available",
    locationId,
    userId,
    agencyId,
  ],
  location: (id) => [...integrationKeys.all, "location", id],
};

export function useAvailableIntegrations(locationId, userId, agencyId, { enabled = true } = {}) {
  return useQuery({
    queryKey: integrationKeys.available(locationId, userId, agencyId),
    queryFn: async () => {
      const params = new URLSearchParams({
        location_id: locationId ?? "",
        user_id: userId ?? "",
      });
      if (agencyId) params.set("agency_id", agencyId);
      const res = await request(GET, "/integrations/available", { query: params.toString() });
      return { integrations: res?.data ?? [], connected: res?.connected ?? [] };
    },
    enabled: !!locationId && enabled,
  });
}

export function useAccountLocation(id, { poll, enabled = true } = {}) {
  return useQuery({
    queryKey: integrationKeys.location(id),
    queryFn: () => request(GET, "/accounts/location", { query: `id=${id}` }),
    enabled: !!id && enabled,
    ...(poll
      ? {
          refetchInterval: (query) =>
            query.state.data?.data?.nango_connected ? false : POLL_INTERVAL_MS,
        }
      : {}),
  });
}

// Shared by Nango.js and IntegrationCard.jsx to confirm a just-launched
// connect attempt. `enabled` should be the "a connect is in flight" flag
// (e.g. `!!loadingProvider`) — the query is fully disabled outside that
// window (no stray one-off request either), and the moment `enabled` flips
// false React Query tears the poll interval down on its own, no manual
// clearInterval/unmount bookkeeping needed. Same query key as
// useAccountLocation, so both share one cache entry.
export function useIntegrationConnectionPoll(id, enabled) {
  return useAccountLocation(id, { poll: true, enabled: !!enabled });
}

export function useCreateNangoConnectSession() {
  return useMutation({
    mutationFn: ({ userInfo, allowedIntegrations }) =>
      request(POST, "/integrations/nango/createConnectSession", {
        payload: {
          end_user: {
            id: userInfo?.userId,
            email: userInfo?.email,
            display_name: userInfo?.userName || "User",
          },
          allowed_integrations: allowedIntegrations,
        },
      }),
  });
}

export function useStoreNangoSession() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/integrations/nango/storeSession", { payload }),
  });
}

export function useDisconnectIntegration() {
  return useMutation({
    mutationFn: ({ profileId, provider, userId, ownerId, integrationId }) => {
      const params = new URLSearchParams({ profile_id: profileId, provider, user_id: userId });
      if (ownerId != null) params.set("owner_id", ownerId);
      if (integrationId != null) params.set("integration_id", integrationId);
      return request(POST, "/integrations/disconnect", { query: params.toString() });
    },
  });
}

export function useDisconnectGhl() {
  return useMutation({
    mutationFn: (id) => request(PUT, "/accounts/disconnect-ghl", { query: `_id=${id}` }),
  });
}

// Settings "Your Tools"/"API Access" shell — the default-prompt prefix/suffix
// shown before a user customizes their auto-reply text. Was a raw
// axios.get(BASE_URL + '/super_admin_settings/') fired on mount in both
// pages/Settings/components/integration/index.js and .../ApiAccess/index.js
// (near-duplicate components, same call).
export const superAdminSettingsKeys = {
  all: ["superAdminSettings"],
};

export function useSuperAdminSettings() {
  return useQuery({
    queryKey: superAdminSettingsKeys.all,
    queryFn: () => request(GET, "/super_admin_settings/"),
  });
}

// POST /accounts/checkcrmagent — despite the verb, this is a status/apiKey
// check fired on mount (and re-fired after create/regenerate). integration/
// index.js and ApiAccess/index.js each independently called it TWICE (once
// for `apiKey`, once for `status`, via a trailing-slash and non-trailing-slash
// URL that hit the same route) — one shared query key here means React Query
// dedupes that back down to a single request, and both fields come off the
// same response envelope (`{ data: { apiKey }, status }`).
export const crmAgentKeys = {
  all: ["crmAgent"],
  check: (id) => [...crmAgentKeys.all, "check", id],
};

export function useCheckCrmAgent(id) {
  return useQuery({
    queryKey: crmAgentKeys.check(id),
    queryFn: () => request(POST, "/accounts/checkcrmagent", { payload: { id } }),
    enabled: !!id,
  });
}

export function useCreateCrmAgent() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/accounts/createcrmagent", { payload }),
  });
}

export function useUpdateStreamingApi() {
  return useMutation({
    mutationFn: ({ id, streamingApi }) =>
      request(PUT, "/accounts/update-streaming-api", { payload: { id, streamingApi } }),
  });
}

// pages/IntegrationApp/Integration/index.jsx's branding-aware integrations
// list — different endpoint from agency.js's useWhitelabelAgency (that one
// hits /agency/getwhitelabel).
export const availableBrandingKeys = {
  all: ["availableBranding"],
  list: (agencyId) => [...availableBrandingKeys.all, agencyId],
};

export function useAvailableBranding(agencyId) {
  return useQuery({
    queryKey: availableBrandingKeys.list(agencyId),
    queryFn: async () => {
      const res = await request(GET, "/integrations/available-branding", {
        query: `agency_id=${agencyId}`,
      });
      return res?.success ? res?.data ?? [] : [];
    },
    enabled: !!agencyId,
  });
}

// integration/index.js's "Connect" button — saves the account's GHL token.
export function useSaveAccountToken() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/accounts/save_account_token", { payload }),
  });
}

// Shared by integration/index.js and ApiAccess/index.js's "Regenerate API
// key" button.
export function useCreateApiKey() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/user/create/api-key", { payload }),
  });
}

// Shared by integration/index.js and ApiAccess/index.js's Slack-connection
// check/fetch/update chain (checkSlackconnection -> fetchSlackconnection on
// mount; updateSlackconnection -> fetchSlackconnection again on dropdown
// select). Modeled as on-demand mutations, not queries, since each fires
// imperatively and chains into the next by passing along its own response.
export function useCheckSlackConnection() {
  return useMutation({
    mutationFn: (profileId) =>
      request(GET, "/accounts/checkslackconnection", { query: `profile_id=${profileId}` }),
  });
}

export function useFetchSlackConnection() {
  return useMutation({
    /** @param {{profileId: string, teamId: string}} args */
    mutationFn: ({ profileId, teamId }) =>
      request(GET, "/accounts/fetchslackconnection", {
        query: `profile_id=${profileId}&team_id=${teamId}`,
      }),
  });
}

export function useUpdateSlackConnection() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/accounts/updateSlackconnection", { payload }),
  });
}
